import {makeAutoObservable, observable, runInAction} from "mobx";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IH2hProvider} from "data/providers/h2h/h2h.provider";
import {
	PoolStatus,
	OpponentUserType,
	OpponentRelationUserType,
	ChallengeResult,
	ChallengeStatus,
	ChallengeRosterStatus,
} from "data/enums/challenge";
import {IGameBar} from "data/stores/game_bar/game_bar.store";
import {IChallengesResponse, IInvitationsResponse} from "data/types/h2h";
import {GameLevel} from "data/enums";

export interface IChallenge {
	opponent: {
		id: number;
		type: OpponentUserType;
		displayName: string;
	};
	gamebar: IGameBar;
	opponentType: OpponentRelationUserType;
	result?: ChallengeResult;
	rosterStatus?: ChallengeRosterStatus;
	projectedPoints?: number;
	status?: ChallengeStatus;
}

export interface IInvitation {
	id: number;
	opponent: IChallenge["opponent"];
	opponentType: OpponentRelationUserType;
	status: ChallengeStatus;
	isViewed: boolean;
	level: GameLevel;
}

export interface IPool {
	id: number;
	status: PoolStatus;
}

export interface IH2hStore {
	get challenges(): IChallenge[];

	get invitations(): IInvitation[];

	get activePoolId(): number | null;

	clearActivePoolId: () => void;
	requestPoolId: (contestId: number) => Promise<IPool>;
	requestChallengeByPool: (poolId: number) => Promise<IChallenge>;
	getChallenges: (contestId: number) => Promise<IChallengesResponse>;
	getInvitations: (contestId: number) => Promise<IInvitationsResponse>;
	inviteFriendToPlay: (contestId: number, friendId: number) => Promise<void>;
	accept: (invite: IInvitation) => Promise<void>;
	delete: (invite: IInvitation) => Promise<void>;
}

@injectable()
export class H2hStore implements IH2hStore {
	@observable _challenges: IChallenge[] = [];
	@observable _invitations: IInvitation[] = [];
	@observable _activePoolId: number | null = null;

	constructor(@inject(Bindings.H2hProvider) private _h2hProvider: IH2hProvider) {
		makeAutoObservable(this);
	}

	get challenges(): IChallenge[] {
		return this._challenges;
	}

	get activePoolId(): number | null {
		return this._activePoolId;
	}

	get invitations(): IInvitation[] {
		return this._invitations;
	}

	requestChallengeByPool = async (poolId: number) => {
		const result = await this._h2hProvider.challengeByPool(poolId);
		const challenge = result.data.success.challenge;

		runInAction(() => {
			this._challenges = this.challenges.filter((it) => {
				return it.opponent.id !== challenge.opponent.id;
			});
			this._challenges.push(challenge);
		});

		return challenge;
	};

	clearActivePoolId = () => {
		this._activePoolId = null;
	};

	requestPoolId = async (contestId: number) => {
		const result = await this._h2hProvider.joinToPool(contestId);
		const pool = result.data.success.pool;
		runInAction(() => {
			if (pool) {
				this._activePoolId = pool.id;
			}
		});
		return pool;
	};

	getChallenges = async (contestId: number) => {
		const result = await this._h2hProvider.getChallenges(contestId);
		const successResponse = result.data.success;
		runInAction(() => {
			this._challenges = successResponse.challenges;
			this._activePoolId = successResponse.poolId;
		});
		return result.data.success;
	};

	getInvitations = async (contestId: number) => {
		const result = await this._h2hProvider.getInvitations(contestId);
		const challenges = result.data.success.challenges;
		runInAction(() => {
			this._invitations = challenges;
		});
		return result.data.success;
	};

	inviteFriendToPlay = async (contestId: number, friendId: number) => {
		await this._h2hProvider.inviteFriendToPlay(contestId, friendId);
	};

	accept = async (invite: IInvitation) => {
		await this._h2hProvider.accept(invite.id);
	};

	delete = async (invite: IInvitation) => {
		await this._h2hProvider.delete(invite.id);
		runInAction(() => {
			this._invitations = this._invitations.filter((it) => it.id !== invite.id);
		});
	};
}
